/**
 * Area of Composite Figures generator (6.MGSR.1.4).
 *
 * Decompose a figure into a rectangle plus a rectangle or triangle, or a
 * rectangle with a rectangular corner removed. Each piece's area is computed
 * with the geometry helpers and then summed or subtracted. Whole-number
 * dimensions keep the total exact and clean.
 *
 * Families:
 *   F1: rectangle + rectangle   (L-shape)
 *   F2: rectangle + triangle    (house/flag shape)
 *   F3: rectangle - corner      (notched rectangle)
 */

import { rat, type Rational } from "../engine/rational";
import { Rng } from "../engine/rng";
import { getTopic } from "../curriculum/curriculum";
import { areaRectangle, areaTriangle } from "../engine/geometry";
import type {
  Difficulty,
  GenerateContext,
  Generator,
  Question,
  QuestionSignature,
} from "./types";

const TOPIC = "area-composite" as const;
const VERSION = "1.0.0";

type Family = "F1" | "F2" | "F3";

interface Cfg {
  readonly families: readonly Family[];
  readonly max: number;
}

const DIFF: Record<Difficulty, Cfg> = {
  easy: { families: ["F1", "F2"], max: 8 },
  standard: { families: ["F1", "F2", "F3"], max: 12 },
  hard: { families: ["F1", "F2", "F3"], max: 18 },
  challenge: { families: ["F2", "F3"], max: 24 },
};

/** An even integer in [2, max], so triangle areas stay whole. */
function evenInt(rng: Rng, max: number): number {
  const hi = Math.floor(max / 2);
  return rng.int(1, hi) * 2;
}

export class AreaCompositeGenerator implements Generator {
  readonly topic = TOPIC;
  readonly version = VERSION;
  readonly supportedDifficulties: readonly Difficulty[] = [
    "easy",
    "standard",
    "hard",
    "challenge",
  ];

  generate(ctx: GenerateContext): Question {
    const topic = getTopic(TOPIC);
    for (let attempt = 0; attempt < 100; attempt++) {
      const rng = new Rng(`${ctx.seed}#${attempt}`);
      const cfg = DIFF[ctx.difficulty];
      const family = rng.pick(cfg.families);

      let prompt: string;
      let latex: string;
      let answer: Rational;
      let stepDesc: string;

      if (family === "F1") {
        const l1 = rng.int(3, cfg.max);
        const w1 = rng.int(2, cfg.max);
        const l2 = rng.int(2, cfg.max);
        const w2 = rng.int(2, cfg.max);
        const a1 = areaRectangle(rat(l1), rat(w1));
        const a2 = areaRectangle(rat(l2), rat(w2));
        answer = a1.add(a2);
        prompt =
          `An L-shaped figure is made of two rectangles: one is ${l1} by ${w1}, ` +
          `the other is ${l2} by ${w2}. Find its total area.`;
        latex = `A = (${l1} \\times ${w1}) + (${l2} \\times ${w2}) = ${a1.toLatex()} + ${a2.toLatex()} = ${answer.toLatex()}`;
        stepDesc = "Split the figure into two rectangles, find each area, and add them.";
      } else if (family === "F2") {
        const l = rng.int(3, cfg.max);
        const w = rng.int(2, cfg.max);
        const triBase = l; // triangle sits on top of the rectangle
        const triHeight = evenInt(rng, cfg.max);
        const aRect = areaRectangle(rat(l), rat(w));
        const aTri = areaTriangle(rat(triBase), rat(triHeight));
        answer = aRect.add(aTri);
        prompt =
          `A figure is a ${l} by ${w} rectangle with a triangle on top. ` +
          `The triangle has base ${triBase} and height ${triHeight}. Find the total area.`;
        latex =
          `A = (${l} \\times ${w}) + \\tfrac{1}{2}(${triBase})(${triHeight}) = ` +
          `${aRect.toLatex()} + ${aTri.toLatex()} = ${answer.toLatex()}`;
        stepDesc = "Find the rectangle's area and the triangle's area, then add them.";
      } else {
        // F3: big rectangle minus a removed rectangular corner.
        const L = rng.int(6, cfg.max + 6);
        const W = rng.int(4, cfg.max);
        const cutL = rng.int(1, L - 2);
        const cutW = rng.int(1, W - 1);
        const whole = areaRectangle(rat(L), rat(W));
        const cut = areaRectangle(rat(cutL), rat(cutW));
        answer = whole.sub(cut);
        prompt =
          `A ${L} by ${W} rectangle has a ${cutL} by ${cutW} rectangular corner removed. ` +
          `Find the area of the remaining figure.`;
        latex =
          `A = (${L} \\times ${W}) - (${cutL} \\times ${cutW}) = ` +
          `${whole.toLatex()} - ${cut.toLatex()} = ${answer.toLatex()}`;
        stepDesc = "Find the whole rectangle's area, then subtract the removed corner.";
      }

      // Keep answers positive and non-trivial.
      if (!answer.isPositive()) continue;

      return {
        id: `${TOPIC}-${ctx.seed}-${family}`,
        generator: TOPIC,
        generatorVersion: VERSION,
        seed: ctx.seed,
        unit: topic.unit,
        topic: TOPIC,
        family,
        difficulty: ctx.difficulty,
        prompt: { text: prompt },
        answerType: "number",
        answer: { kind: "number", value: answer },
        steps: [{ description: stepDesc, latex }],
        hints: [
          { level: 1, text: "Break the figure into rectangles and triangles you know how to measure." },
          { level: 2, text: "Add the pieces for a combined figure; subtract for a removed piece." },
        ],
        subskills: topic.subskills,
      };
    }
    throw new Error(`${TOPIC}: failed after 100 attempts (${ctx.seed}, ${ctx.difficulty})`);
  }

  signature(q: Question): QuestionSignature {
    const structure = q.answer.kind === "number" ? q.answer.value.key() : q.answer.kind;
    return { generator: TOPIC, family: q.family, difficulty: q.difficulty, structure };
  }
}

export const areaComposite = new AreaCompositeGenerator();
